import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  FileText, Download, FileSpreadsheet, Loader2, Shield, Building2,
  Calendar, Users, Database, Lock, Globe, Clock, Eye, RefreshCw,
  CheckCircle, AlertTriangle, Info, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROPAPage = () => {
  const { user } = useOutletContext();
  const [ropaData, setRopaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchROPAData();
  }, []);

  const fetchROPAData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ropa/report`, { withCredentials: true });
      setRopaData(response.data);
    } catch (error) {
      console.error('Error fetching ROPA data:', error);
      toast.error('Erro ao carregar dados ROPA');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ropa/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROPA_Privaxia_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório ROPA exportado em PDF');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar ROPA em PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ropa/export/excel`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROPA_Privaxia_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório ROPA exportado em Excel');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Erro ao exportar ROPA em Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setDetailsDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Ativo', variant: 'default', className: 'bg-green-500' },
      draft: { label: 'Rascunho', variant: 'secondary', className: 'bg-gray-500' },
      approved: { label: 'Aprovado', variant: 'default', className: 'bg-blue-500' },
      executed: { label: 'Executado', variant: 'default', className: 'bg-purple-500' },
      pending: { label: 'Pendente', variant: 'secondary', className: 'bg-yellow-500' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary', className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (!user || !['admin', 'dpo', 'super_admin', 'tenant_admin'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <p className="text-red-500 font-medium">Acesso Restrito</p>
          <p className="text-slate-500 text-sm">Apenas DPO e Administradores podem aceder ao ROPA</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
        <span className="ml-2">A carregar dados ROPA...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ropa-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Registo de Atividades de Tratamento
          </h1>
          <p className="text-slate-600">
            ROPA - Artigo 30º do RGPD | Última atualização: {ropaData?.generated_at ? new Date(ropaData.generated_at).toLocaleString('pt-PT') : 'N/A'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchROPAData}
            data-testid="refresh-ropa-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button
            onClick={handleExportPDF}
            disabled={exportingPDF}
            data-testid="export-ropa-pdf-btn"
            className="bg-red-600 hover:bg-red-700"
          >
            {exportingPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Exportar PDF
          </Button>
          <Button
            onClick={handleExportExcel}
            disabled={exportingExcel}
            data-testid="export-ropa-excel-btn"
            className="bg-green-600 hover:bg-green-700"
          >
            {exportingExcel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Organization Info */}
      <Card className="border-l-4 border-l-[#10B981]">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5 text-[#10B981]" />
            Responsável pelo Tratamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500">Organização</p>
              <p className="font-medium">{ropaData?.organization?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Contacto</p>
              <p className="font-medium">{ropaData?.organization?.contact || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">DPO</p>
              <p className="font-medium">{ropaData?.organization?.dpo_contact || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Atividades</p>
                <p className="text-2xl font-bold text-[#0F172A]">{ropaData?.statistics?.total_activities || 0}</p>
              </div>
              <Database className="w-8 h-8 text-[#3B82F6]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Consentimentos</p>
                <p className="text-2xl font-bold text-[#10B981]">{ropaData?.statistics?.total_consents || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ativos</p>
                <p className="text-2xl font-bold text-[#10B981]">{ropaData?.statistics?.active_consents || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Revogados</p>
                <p className="text-2xl font-bold text-[#EF4444]">{ropaData?.statistics?.revoked_consents || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Grupos</p>
                <p className="text-2xl font-bold text-[#8B5CF6]">{ropaData?.statistics?.total_groups || 0}</p>
              </div>
              <Users className="w-8 h-8 text-[#8B5CF6]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Campanhas</p>
                <p className="text-2xl font-bold text-[#F59E0B]">{ropaData?.statistics?.total_campaigns || 0}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-[#F59E0B]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activities Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="activities">Atividades de Tratamento</TabsTrigger>
          <TabsTrigger value="legal">Base Legal</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-[#3B82F6]" />
                Resumo do Registo
              </CardTitle>
              <CardDescription>
                Visão geral das atividades de tratamento de dados pessoais
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Tratamentos Principais</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Gestão de Consentimentos de Marketing</li>
                      <li>• Processamento de Pedidos RTBF (Art. 17º)</li>
                      <li>• Campanhas de Marketing</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Medidas de Segurança</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• Encriptação de dados</li>
                      <li>• Controlo de acessos baseado em roles</li>
                      <li>• Logs de auditoria completos</li>
                      <li>• Backups regulares</li>
                    </ul>
                  </div>
                </div>
                <Separator />
                <div className="text-sm text-slate-500">
                  <p><strong>Gerado por:</strong> {ropaData?.generated_by || 'Sistema'}</p>
                  <p><strong>Data:</strong> {ropaData?.generated_at ? new Date(ropaData.generated_at).toLocaleString('pt-PT') : 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>Atividades de Tratamento</CardTitle>
              <CardDescription>
                Lista detalhada de todas as atividades de tratamento registadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Atividade</TableHead>
                    <TableHead>Finalidade</TableHead>
                    <TableHead>Base Legal</TableHead>
                    <TableHead>Categorias de Dados</TableHead>
                    <TableHead>Registos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ropaData?.activities?.map((activity, index) => (
                    <TableRow key={activity.activity_id || index}>
                      <TableCell className="font-medium">{activity.activity_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{activity.purpose}</TableCell>
                      <TableCell className="text-sm">{activity.legal_basis?.substring(0, 30)}...</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(activity.data_categories || []).slice(0, 2).map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                          ))}
                          {(activity.data_categories || []).length > 2 && (
                            <Badge variant="outline" className="text-xs">+{activity.data_categories.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{activity.total_records || 0}</TableCell>
                      <TableCell>{getStatusBadge(activity.status || 'active')}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(activity)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#10B981]" />
                Fundamentação Legal
              </CardTitle>
              <CardDescription>
                Base legal aplicável às atividades de tratamento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Art. 6º(1)(a) - Consentimento</h4>
                  <p className="text-sm text-slate-600">
                    O titular dos dados deu o seu consentimento para o tratamento dos seus dados pessoais 
                    para uma ou mais finalidades específicas.
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>Atividades abrangidas:</strong> Gestão de Consentimentos, Campanhas de Marketing
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Art. 17º - Direito ao Apagamento</h4>
                  <p className="text-sm text-slate-600">
                    O titular dos dados tem o direito de obter do responsável pelo tratamento o apagamento 
                    dos seus dados pessoais.
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>Atividades abrangidas:</strong> Processamento de Pedidos RTBF
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Art. 30º - Registos das Atividades de Tratamento</h4>
                  <p className="text-sm text-slate-600">
                    Cada responsável pelo tratamento deve conservar um registo de todas as atividades 
                    de tratamento sob a sua responsabilidade.
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>Este documento:</strong> Constitui o registo formal exigido pelo RGPD
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activity Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{selectedActivity?.activity_name}</DialogTitle>
            <DialogDescription>Detalhes completos da atividade de tratamento</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            {selectedActivity && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">ID da Atividade</p>
                    <p className="font-mono text-sm">{selectedActivity.activity_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Estado</p>
                    {getStatusBadge(selectedActivity.status || 'active')}
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Finalidade</p>
                  <p>{selectedActivity.purpose}</p>
                </div>
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Base Legal</p>
                  <p>{selectedActivity.legal_basis}</p>
                </div>
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Categorias de Dados</p>
                  <div className="flex flex-wrap gap-1">
                    {(selectedActivity.data_categories || []).map((cat, i) => (
                      <Badge key={i} variant="outline">{cat}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Categorias de Titulares</p>
                  <p>{selectedActivity.data_subjects_categories}</p>
                </div>
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Destinatários</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(selectedActivity.recipients) ? selectedActivity.recipients : [selectedActivity.recipients]).map((rec, i) => (
                      <Badge key={i} variant="secondary">{rec}</Badge>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Transferências Internacionais</p>
                    <p>{selectedActivity.third_country_transfers}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Prazo de Conservação</p>
                    <p>{selectedActivity.retention_period}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-slate-500 mb-1">Medidas de Segurança</p>
                  <div className="flex flex-wrap gap-1">
                    {(selectedActivity.security_measures || []).map((measure, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800">{measure}</Badge>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Origem dos Dados</p>
                    <p>{selectedActivity.data_source}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Nº de Registos</p>
                    <p className="text-xl font-bold">{selectedActivity.total_records || 0}</p>
                  </div>
                </div>

                {selectedActivity.dpo_approved !== undefined && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Aprovação DPO:</strong> {selectedActivity.dpo_approved ? 'Aprovado ✓' : 'Pendente'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ROPAPage;
